import { useRef, useEffect, useState } from "react";
import projects from '../data/dataPortfolio';

const usePortfolioLogic = () => {
  const [screenSize, setScreenSize] = useState("desktop");
  const carouselRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragLimits, setDragLimits] = useState({ left: 0, right: 0 });

  // Detecta tamanho de tela
  useEffect(() => {
    const updateScreenSize = () => {
      if (window.innerWidth <= 768) {
        setScreenSize("mobile");
      } else if (window.innerWidth <= 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  // Define limites de drag (mobile)
  useEffect(() => {
    if (screenSize !== "desktop") {
      const calculateDragConstraints = () => {
        if (carouselRef.current) {
          const container = carouselRef.current;
          const scrollWidth = container.scrollWidth;
          const visibleWidth = container.offsetWidth;
          const maxDrag = scrollWidth - visibleWidth;

          setDragLimits({
            left: -maxDrag,
            right: 0,
          });
        }
      };

      calculateDragConstraints();
      window.addEventListener("resize", calculateDragConstraints);
      return () => window.removeEventListener("resize", calculateDragConstraints);
    }
  }, [screenSize]);

  // Calcula index ativo ao scrollar
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      const containerCenter = carousel.offsetWidth / 2;
      const scrollLeft = carousel.scrollLeft;

      let closestIndex = 0;
      let minDistance = Infinity;

      Array.from(carousel.children).forEach((child, index) => {
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const distance = Math.abs((childCenter - scrollLeft) - containerCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
    };

    carousel.addEventListener("scroll", handleScroll);
    const handleInitialScroll = () => {
      handleScroll();
      carousel.removeEventListener("scroll", handleInitialScroll);
    };
    carousel.addEventListener("scroll", handleInitialScroll);

    return () => {
      carousel.removeEventListener("scroll", handleScroll);
      carousel.removeEventListener("scroll", handleInitialScroll);
    };
  }, [screenSize]);

  // Centraliza o segundo card ao carregar (desktop e mobile)
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const scrollToSecondCard = () => {
      const secondCard = carousel.children[1];
      if (secondCard) {
        const secondCardPosition = secondCard.offsetLeft;
        const offsetToCenter =
          secondCardPosition - (carousel.offsetWidth - secondCard.offsetWidth) / 2;

        requestAnimationFrame(() => {
          carousel.scrollTo({
            left: offsetToCenter,
            behavior: 'smooth',
          });
        });
      }
    };

    window.addEventListener("load", scrollToSecondCard);
    return () => window.removeEventListener("load", scrollToSecondCard);
  }, [screenSize]);

  // Drag para desktop
  useEffect(() => {
    if (screenSize !== "desktop") return;
    const carousel = carouselRef.current;
    if (!carousel) return;

    let isDragging = false;
    let startX = 0;
    let scrollLeftStart = 0;

    const handleMouseDown = (e) => {
      isDragging = true;
      startX = e.pageX - carousel.offsetLeft;
      scrollLeftStart = carousel.scrollLeft;
      carousel.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startX) * 1.5;
      carousel.scrollLeft = scrollLeftStart - walk;
    };

    const handleMouseUp = () => {
      isDragging = false;
      carousel.style.cursor = 'grab';
    };

    carousel.addEventListener('mousedown', handleMouseDown);
    carousel.addEventListener('mousemove', handleMouseMove);
    carousel.addEventListener('mouseup', handleMouseUp);
    carousel.addEventListener('mouseleave', handleMouseUp);

    return () => {
      carousel.removeEventListener('mousedown', handleMouseDown);
      carousel.removeEventListener('mousemove', handleMouseMove);
      carousel.removeEventListener('mouseup', handleMouseUp);
      carousel.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [screenSize]);

  return {
    screenSize,
    carouselRef,
    dragConstraints: dragLimits,
    activeIndex,
    totalProjects: projects.length,
  };
};

export default usePortfolioLogic;
